import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useConfigStore } from '@/stores/configStore'
import { makeId, idPrefixes, uuid } from '@/services/uuid'
import {
  offerSchema,
  FunnelDefinitionRootNode,
  Offer,
  TriggeredAction
} from '@/types/config'
import { defaultFormFields } from '@/modules/offer/data/formFields'

export { defaultFormFields }

const formPartsDefault = {
  title: 'Opt-in to our glorious form',
  description: 'Enter your email below to receive free stuff.',
  buttonText: 'Join Now'
}

const offerPartsDefault = {
  title: 'Learn more about our amazing offer',
  description: `It's very, very cool. Click below to learn more.`,
  buttonText: 'Learn More'
}
type TriggeredActionSettingsInput = {
  partnerName?: string | null
  formIds?: unknown
  listIds?: unknown
  tagIds?: unknown
  eventIds?: string | string[]
  eventMetadata?: Array<{ key: string; value: string }>
  automationIds?: string | string[]
  actionId?: string
  url?: string | null
  method?: 'POST' | 'PUT' | 'PATCH'
  customPayload?: string
  timeoutMs?: number
  thanksForConvertingText?: string
  redirectFunnel?: FunnelDefinitionRootNode
  formSettings?: {
    showLabels: boolean
  }
  fields?: Extract<TriggeredAction, { type: 'form' }>['settings']['fields']
}

export const normalizeTriggeredActionIds = (value: unknown): string[] => {
  const values = Array.isArray(value) ? value : [value]

  return values
    .filter((id): id is string | number =>
      typeof id === 'string' || typeof id === 'number'
    )
    .flatMap((id) => String(id).split(/[,\n]/))
    .map((id) => id.trim())
    .filter(Boolean)
}

const createRedirectFunnelDefault = () => ({
  _uuid: uuid(),
  type: 'root' as const,
  child: []
})

const buildSharedFormActionSettings = (existingSettings: TriggeredActionSettingsInput = {}) => ({
  thanksForConvertingText: existingSettings.thanksForConvertingText || '',
  redirectFunnel:
    existingSettings.redirectFunnel || createRedirectFunnelDefault(),
  formSettings: existingSettings.formSettings || {
    showLabels: false
  },
  fields: existingSettings.fields || defaultFormFields()
})

const buildTriggeredAction = (
  type: Exclude<TriggeredAction['type'], 'link'>,
  existingSettings: TriggeredActionSettingsInput = {}
): TriggeredAction => {
  const sharedSettings = buildSharedFormActionSettings(existingSettings)

  switch (type) {
    case 'form':
      return {
        type,
        settings: {
          partnerName: existingSettings.partnerName ?? '',
          formIds: normalizeTriggeredActionIds(existingSettings.formIds),
          ...sharedSettings
        }
      }
    case 'list':
      return {
        type,
        settings: {
          partnerName: existingSettings.partnerName || '',
          listIds: normalizeTriggeredActionIds(existingSettings.listIds),
          ...sharedSettings
        }
      }
    case 'tag':
      return {
        type,
        settings: {
          partnerName: existingSettings.partnerName || '',
          tagIds: normalizeTriggeredActionIds(existingSettings.tagIds),
          ...sharedSettings
        }
      }
    case 'event':
      return {
        type,
        settings: {
          partnerName: existingSettings.partnerName || '',
          eventIds: existingSettings.eventIds || [],
          eventMetadata: existingSettings.eventMetadata || [],
          ...sharedSettings
        }
      }
    case 'automation':
      return {
        type,
        settings: {
          partnerName: existingSettings.partnerName || '',
          automationIds: existingSettings.automationIds || [],
          ...sharedSettings
        }
      }
    case 'contact':
      return {
        type,
        settings: {
          partnerName: existingSettings.partnerName || '',
          ...sharedSettings
        }
      }
    case 'webhook':
      return {
        type,
        settings: {
          actionId: existingSettings.actionId || uuid(),
          url: existingSettings.url || '',
          method: existingSettings.method || 'POST',
          customPayload: existingSettings.customPayload || '',
          timeoutMs: existingSettings.timeoutMs ?? 10000,
          ...sharedSettings
        }
      }
  }
}

export const useOfferStore = defineStore('offer', () => {
  const configStore = useConfigStore()
  const { config, isLoading, isError } = storeToRefs(configStore)

  const offers = computed(() => {
    return (config.value?.rightCta?.offers || []).sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  })
  const optinOffers = computed(() => offers.value.filter((o) => isForm(o)))
  const ctaOffers = computed(() => offers.value.filter((o) => !isForm(o)))

  const getById = (id: string) => offers.value.find((offer) => offer.id === id)

  const setTriggeredActionType = (
    offer: Offer,
    type: Exclude<TriggeredAction['type'], 'link'>
  ) => {
    offer.triggeredAction = buildTriggeredAction(type, offer.triggeredAction?.settings)
  }

  const ensureTriggeredActionDefaults = (offer: Offer) => {
    if (!offer?.triggeredAction || offer.triggeredAction.type === 'link') return

    offer.triggeredAction = buildTriggeredAction(
      offer.triggeredAction.type,
      offer.triggeredAction.settings
    )
  }

  const create = ({
    type,
    offer
  }: {
    type: 'form' | 'offer'
    offer?: Partial<Offer>
  }) => {
    const namePrefix = type === 'form' ? 'Form' : 'Offer'
    const partsDefault = type === 'form' ? formPartsDefault : offerPartsDefault
    const triggeredAction =
      type === 'form'
        ? buildTriggeredAction('form')
        : { type: 'link', settings: { url: '', open: false } }
    const newOffer = offerSchema.parse({
      id: makeId(idPrefixes.OFFER),
      name: `New ${namePrefix} ${offers.value.length + 1}`,
      isEnabled: true,
      parts: partsDefault,
      triggeredAction,
      ...offer
    }) as Offer

    config.value?.rightCta?.offers?.push(newOffer)

    recordAnalyticsEvent(
      type === 'form'
        ? AnalyticsEventTypes.FORM_CREATED
        : AnalyticsEventTypes.OFFER_CREATED,
      {
        id: newOffer.id,
        name: newOffer.name
      }
    )

    return newOffer
  }

  const destroy = (offer: Offer) => {
    if (!config.value?.rightCta?.offers) return
    const foundIndex = config.value.rightCta.offers.findIndex(
      (o) => o.id == offer.id
    )
    if (foundIndex === -1) return // this should really never happen

    recordAnalyticsEvent(AnalyticsEventTypes.OFFER_DELETED, {
      id: offer.id,
      name: offer.name
    })

    funnelDefinitionForEachFlow((node) => {
      if (node.type === 'offer' && node.offerId === offer.id) {
        node.offerId = null
      }
    })

    config.value?.rightCta?.offers?.splice(foundIndex, 1)
  }

  const duplicate = (offer: Offer) => {
    const newOffer = {
      ...klona(offer),
      id: makeId(idPrefixes.OFFER),
      name: `${offer.name} (Copy)`
    }

    if (newOffer.triggeredAction?.type === 'webhook') {
      newOffer.triggeredAction.settings.actionId = uuid()
    }

    config.value?.rightCta?.offers?.push(newOffer)

    return newOffer
  }

  return {
    offers,
    optinOffers,
    ctaOffers,
    getById,
    isLoading,
    isError,
    setTriggeredActionType,
    ensureTriggeredActionDefaults,
    create,
    destroy,
    duplicate
  }
})
